import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faAmbulance } from '@fortawesome/free-solid-svg-icons';
import { Player, TeamInLeague } from '../../../domain/entities';
import { View } from '../../../App';
import { PlayerFace } from '../../shared/PlayerFace';
import { TeamBadge } from '../../shared/TeamBadge';
import { PlayerBadgesSection } from './PlayerBadgesSection';
import { soundService } from '../../../services/SoundService';
import { generateFlexibilityAssessment } from '../../../utils/utilities';

interface PlayerDetailHeaderProps {
    player: Player;
    team: TeamInLeague;
    isPlayerTeamPlayer: boolean;
    isStarter: boolean;
    tacticalPosition: string;
    isPlayerTeam: boolean;
    playerTeamName: string;
    leagueAverageSkill: number;
    leagueName: string;
    pushView: (view: View) => void;
    onContractClick?: () => void;
}

export const PlayerDetailHeader: React.FC<PlayerDetailHeaderProps> = ({
    player, team, isStarter, tacticalPosition, isPlayerTeam, playerTeamName, leagueAverageSkill, leagueName, pushView, onContractClick
}) => {
    return (
        <div className="player-detail-compact-header">
            <div className="player-detail-compact-header-content">
                {/* Row 1: Face + Name + Meta */}
                <div className="player-detail-compact-header-row-1">
                    <PlayerFace player={player} teamColor={team?.colors.primary} />
                    <div className="player-detail-compact-name-section">
                        <h2>
                            {player.name}
                            {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} style={{ marginLeft: '8px' }} />}
                            {player.isListed && <span style={{ marginLeft: '12px', fontSize: '0.75em', fontWeight: 'normal', color: 'var(--warning-color)', border: '1px solid var(--warning-color)', padding: '2px 6px', borderRadius: '4px' }}>Listed</span>}
                        </h2>
                        <div className="player-detail-compact-meta">
                            <span>{player.age} yrs</span>
                            <span>•</span>
                            <span>{player.position}</span>
                            <span>•</span>
                            {team ? (
                                <>
                                    <button className="link-style" onClick={() => { soundService.playClick(); pushView({ type: 'teamDetail', title: `Team: ${team.name}`, teamName: team.name }); }}>
                                        <span className={team.name === playerTeamName ? 'player-team-name' : ''}>{team.badge && <TeamBadge badge={team.badge} color={team.colors.primary} size="sm" />}{team.name}</span>
                                    </button>
                                    <span>•</span>
                                    <span>{isPlayerTeam ? (isStarter ? `Starter (${tacticalPosition})` : 'Reserve') : 'Opposition Player'}</span>
                                </>
                            ) : (
                                <span style={{ color: 'var(--positive-color)', fontWeight: 'bold' }}>🆓 FREE AGENT</span>
                            )}
                            {onContractClick && (
                                <>
                                    <span>•</span>
                                    <button
                                        className="link-style contract-info-button"
                                        onClick={() => { soundService.playClick(); onContractClick(); }}
                                        title="View contract details"
                                        aria-label="View contract details"
                                    >
                                        <FontAwesomeIcon icon={faFileContract} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Row 2: Flexibility + Badges */}
                <div className="player-detail-compact-header-row-2">
                    <div style={{ fontSize: '0.85em', color: 'var(--text-secondary-color)', fontStyle: 'italic' }}>
                        {generateFlexibilityAssessment(player)}
                    </div>
                    <PlayerBadgesSection player={player} leagueAverageSkill={leagueAverageSkill} leagueName={leagueName} />
                </div>
            </div>
        </div>
    );
};